import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { lookupClients } from '../api/calculations.api'
import type { CalcLookupClient } from '../types/calculation.types'

/**
 * AP-37 4.1 — **Kunde aus dem Stamm suchen** (Name oder Nummer). Über die
 * Auswahlhilfe der Kalkulation — ein Kalkulant braucht kein CRM-Recht.
 */
export function PartnerAuswahl({
  wert,
  onWahl,
  gesperrt,
  klasse,
}: {
  wert: { id: string; name: string } | null
  onWahl: (partner: CalcLookupClient | null) => void
  gesperrt?: boolean
  klasse: string
}) {
  const [suche, setSuche] = useState('')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useState<CalcLookupClient[]>([])
  const rahmen = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!offen) return
    let aktiv = true
    const t = setTimeout(() => {
      lookupClients(suche.trim() || undefined)
        .then((r) => aktiv && setTreffer(r))
        .catch(() => aktiv && setTreffer([]))
    }, 200)
    return () => {
      aktiv = false
      clearTimeout(t)
    }
  }, [suche, offen])

  useEffect(() => {
    if (!offen) return
    const weg = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) setOffen(false)
    }
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [offen])

  if (wert && !offen) {
    return (
      <div className={`${klasse} flex items-center justify-between gap-2`}>
        <span className="truncate">{wert.name}</span>
        {!gesperrt && (
          <span className="flex shrink-0 items-center gap-1">
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setOffen(true)}>
              ändern
            </button>
            <button type="button" className="text-muted-foreground hover:text-destructive" aria-label="Kunde entfernen" onClick={() => onWahl(null)}>
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>
    )
  }

  return (
    <div ref={rahmen} className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        className={`${klasse} pl-8`}
        placeholder="Kunde suchen (Name oder Nummer) …"
        value={suche}
        disabled={gesperrt}
        onFocus={() => setOffen(true)}
        onChange={(e) => {
          setSuche(e.target.value)
          setOffen(true)
        }}
      />
      {offen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-border p-1 shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
          {treffer.length === 0 && <p className="px-2 py-1.5 text-sm text-muted-foreground">Keine Treffer</p>}
          {treffer.map((p) => (
            <button
              key={p.id}
              type="button"
              className="flex w-full items-baseline justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onWahl(p)
                setSuche('')
                setOffen(false)
              }}
            >
              <span className="truncate">{p.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{p.clientNumber}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
